import { useState } from "react";
import { isMobile } from "react-device-detect";
import { DndProvider } from "react-dnd";
import { HTML5Backend } from "react-dnd-html5-backend";
import { TouchBackend } from "react-dnd-touch-backend";
import { Route, BrowserRouter as Router, Switch } from "react-router-dom";
import styled from "styled-components";
import { QueryParameters, Routes } from "@lifeat/shared/system/RouteConstants";
import AppOfflineAlertModal from "@common/LifeAtModal/AppOfflineAlertModal/AppOfflineAlertModal";
import LifeAtToastV2Provider from "@common/LifeAtToastV2/LifeAtToastV2Provider";
import { AppearanceContextProvider } from "@contexts/AppearanceContext";
import { CallToActionContextProvider } from "@contexts/CallToActionContext";
import { DatabaseContext } from "@contexts/DatabaseContext";
import GlobalAudioContext from "@contexts/GlobalAudioContext";
import { ToastListContextProvider } from "@contexts/ToastListContext";
import { UserContextProvider } from "@contexts/UserContext";
import { VersionContextProvider } from "@contexts/VersionContext";
import "@desktop/ToDesktop.scss";
import useAppOfflineStatus from "@hooks/useAppOfflineStatus";
import useDatabaseState from "@hooks/useDatabaseState";
import { AdminPage } from "@pages/AdminPage";
import { AdminSpaceCategoryPage } from "@pages/AdminSpaceCategoryPage";
import { AdminUserByEmailPage } from "@pages/AdminUserByEmailPage";
import { AdminUserByUidPage } from "@pages/AdminUserByUidPage";
import CancellationPolicyPage from "@pages/CancellationPolicyPage";
import CreatorsApplyPage from "@pages/Creators/CreatorsApplyPage";
import CreatorsPage from "@pages/Creators/CreatorsPage";
import DeleteAccountPage from "@pages/DeleteAccountPage";
import DynamicLobbyPage from "@pages/DynamicLobbyPage";
import EmailVerificationLandingPage from "@pages/EmailVerificationLandingPage";
import ExternalRedirectPage from "@pages/ExternalRedirectPage";
import LoadingRedirectPage from "@pages/LoadingRedirectPage";
import ManageSubscriptionPage from "@pages/ManageSubscriptionPage";
import NotFoundPage from "@pages/NotFoundPage";
import { OAuthGoogleCalendarCallbackPage } from "@pages/OAuthGoogleCalendarCallbackPage";
import { OAuthGoogleCallbackPage } from "@pages/OAuthGoogleCallbackPage";
import { OAuthOutlookCalendarCallbackPage } from "@pages/OAuthOutlookCalendarCallbackPage";
import OnboardingPage from "@pages/OnboardingPage";
import { OnboardingVideoPage } from "@pages/OnboardingVideoPage";
import RoomAccessPage from "@pages/RoomAccessPage";
import StressTestPage from "@pages/StressTestPage";
import CancelSubscriptionPage from "@pages/SubscriptionPages/CancelSubscriptionPage";
import UpgradeSuccessPage from "@pages/UpgradeSuccessPage";
import UserSignUpWithReferralPage from "@pages/UserSignUpWithReferralPage/UserSignUpWithReferralPage";
import * as Tooltip from "@radix-ui/react-tooltip";
import { logError } from "@service/ConsoleLoggerService";
import { DatabaseService } from "@service/DatabaseService";
import { buildRoute, isProductionRoute } from "@util/RouteUtil";
import { BrowserUtil, RouteUtil } from "@util/index";
import { CannyProvider } from "../features/canny/CannyProvider";
import DesktopAppMainWindow from "../features/desktop-app/views/MainWindow/DesktopAppMainWindow";
import DesktopAppMenubarWindow from "../features/desktop-app/views/MenubarWindow/DesktopAppMenubarWindow";
import { ChatContextProvider } from "../features/room/room-chat/ChatContext";
import PricingPage from "../features/upgrade-flow/components/PricingPage";
import ProTierUpgradePage from "../features/upgrade-flow/components/ProTierUpgradeBlock";
import { GlobalStyles } from "../globalStyles";
import "./App.scss";
import LifeAtApp from "./LifeAtApp/LifeAtApp";

const TestIndicator = styled.div`
    position: fixed;
    top: 0;
    width: 100dvw;
    font-size: 12px;
    font-weight: bold;
    text-align: center;
    color: white;
    background: repeating-linear-gradient(45deg, #606dbc, #606dbc 10px, #465298 10px, #465298 20px);
    z-index: 1;
`;

// For Electron Desktop App (ToDesktop) we make the homepage lifeat.io/app. But
// if a user wants to access someone else's Room via Chrome Deep Linking, it will
// take them too lifeat.io/app/room/{RoomId}, so we need to redirect the user.
if (location.pathname.includes(`/app/room/`)) {
    BrowserUtil.redirectTo(location.pathname.split("/app")[1]);
}

export default function App() {
    const database = DatabaseService();

    const [masterVolume, setMasterVolume] = useState(1);

    // If the user accessed the site using the email verification link + button, the URL
    // will contain their email (necessary to keep them logged in automatically, see
    // UserAccountModal.tsx). We redirect to the /email-verify route so the user can
    // either continue in the browser or open the desktop app.
    if (RouteUtil.queryParamPresent(QueryParameters.Email)) {
        // Note: for some reason `useQueryParam` hook throws an error when used in App.tsx
        const redirUrl = RouteUtil.getQueryParamValue(QueryParameters.RedirectSuccess);
        const redirParam = `?${QueryParameters.RedirectSuccess}=${redirUrl}`;
        window.location.href = `/${Routes.EmailVerifyPage}${redirUrl ? redirParam : ""}`;
    }

    // Short circuit so that if the user is anywhere in the app and internet
    // access is lost, we automatically display the offline modal.
    const isOffline = useAppOfflineStatus();
    const databaseState = useDatabaseState();
    if (isOffline) {
        return <AppOfflineAlertModal />;
    } else if (databaseState === "disconnected") {
        logError("Database disconnected.");
    }

    return (
        <DatabaseContext.Provider value={database}>
            <UserContextProvider>
                <AppearanceContextProvider>
                    <Tooltip.Provider>
                        <LifeAtToastV2Provider>
                            <VersionContextProvider>
                                <ToastListContextProvider>
                                    <GlobalAudioContext.Provider value={{ masterVolume, setMasterVolume }}>
                                        <ChatContextProvider>
                                            <CannyProvider>
                                                <DndProvider backend={isMobile ? TouchBackend : HTML5Backend}>
                                                    <GlobalStyles />
                                                    <Router>
                                                        <CallToActionContextProvider>
                                                            <Switch>
                                                                {/* App View Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.App)}
                                                                    component={LifeAtApp}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.App,
                                                                        Routes.Room,
                                                                        ":roomId",
                                                                    )}
                                                                />
                                                                {/* TODO: need to make this a query param */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.Room, ":roomId")}
                                                                    component={LifeAtApp}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.RoomAccess, ":roomId")}
                                                                    component={RoomAccessPage}
                                                                />
                                                                {/** @deprecated Re-routes automatically to base app + query param --> /?space=123 */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.Space, ":spaceId")}
                                                                    component={LifeAtApp}
                                                                />
                                                                <Route
                                                                    path={buildRoute(
                                                                        Routes.ReferralPage,
                                                                        ":referralCode",
                                                                    )}
                                                                    render={(props) => (
                                                                        <UserSignUpWithReferralPage {...props} />
                                                                    )}
                                                                />
                                                                {/* Desktop App Page */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.DesktopMain)}
                                                                    component={DesktopAppMainWindow}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.DesktopMenubar)}
                                                                    component={DesktopAppMenubarWindow}
                                                                />
                                                                {/* Auth Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.OAuthGoogleCallback)}
                                                                    component={OAuthGoogleCallbackPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.DesktopMain,
                                                                        Routes.OAuthGoogleCallback,
                                                                    )}
                                                                    component={OAuthGoogleCallbackPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.OAuthGoogleCalendarCallback,
                                                                    )}
                                                                    component={OAuthGoogleCalendarCallbackPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.DesktopMain,
                                                                        Routes.OAuthGoogleCalendarCallback,
                                                                    )}
                                                                    component={OAuthGoogleCalendarCallbackPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.OAuthOutlookCalendarCallback,
                                                                    )}
                                                                    component={OAuthOutlookCalendarCallbackPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.DesktopMain,
                                                                        Routes.OAuthOutlookCalendarCallback,
                                                                    )}
                                                                    component={OAuthOutlookCalendarCallbackPage}
                                                                />
                                                                {/* Account Deletion Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.DeleteAccountPage)}
                                                                    component={DeleteAccountPage}
                                                                />
                                                                {/* Onboarding Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.Signup)}
                                                                    component={OnboardingPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.OnboardingVideo)}
                                                                    component={OnboardingVideoPage}
                                                                />
                                                                {/* Utility Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.RedirectLoading)}
                                                                    component={LoadingRedirectPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.ExternalRedirect)}
                                                                    component={ExternalRedirectPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.EmailVerifyPage)}
                                                                    component={EmailVerificationLandingPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.CancellationPolicy)}
                                                                    component={CancellationPolicyPage}
                                                                />
                                                                {/* Paid Subscriptions Pages */}
                                                                {/* NOTE: /manage is linked in Stripe */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.ManageSubscription)}
                                                                    component={ManageSubscriptionPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.UpgradeSuccessPage)}
                                                                    component={UpgradeSuccessPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.SubscriptionCancel)}
                                                                    component={CancelSubscriptionPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.UpgradePage)}
                                                                    component={PricingPage}
                                                                />
                                                                {/* NOTE: /upgrade-modal is embedded in upgrade/cancel webflow page */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.UpgradeModalPage)}
                                                                    component={ProTierUpgradePage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.PricingTierPage)}
                                                                    component={PricingPage}
                                                                />
                                                                {/* Creator Pages */}
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.CreatorsApplyPage)}
                                                                    component={CreatorsApplyPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.CreatorsPage)}
                                                                    component={CreatorsPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.DynamicLobby)}
                                                                    component={DynamicLobbyPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.StressTest)}
                                                                    component={StressTestPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.AdminUserByUid, ":uid")}
                                                                    component={AdminUserByUidPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(
                                                                        Routes.AdminUserByEmail,
                                                                        ":emailBase64",
                                                                    )}
                                                                    component={AdminUserByEmailPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.AdminSpace, ":category")}
                                                                    component={AdminSpaceCategoryPage}
                                                                />
                                                                <Route
                                                                    exact
                                                                    path={buildRoute(Routes.Admin)}
                                                                    component={AdminPage}
                                                                />
                                                                {/* NOTE: All other routes not defined will go to our error page. */}
                                                                <Route component={NotFoundPage} />
                                                            </Switch>
                                                        </CallToActionContextProvider>
                                                    </Router>
                                                    <div id="dropdown-portal" />
                                                    {!isProductionRoute() && (
                                                        <TestIndicator>TEST ENVIRONMENT</TestIndicator>
                                                    )}
                                                </DndProvider>
                                            </CannyProvider>
                                        </ChatContextProvider>
                                    </GlobalAudioContext.Provider>
                                </ToastListContextProvider>
                            </VersionContextProvider>
                        </LifeAtToastV2Provider>
                    </Tooltip.Provider>
                </AppearanceContextProvider>
            </UserContextProvider>
        </DatabaseContext.Provider>
    );
}
